import { useEffect, useRef, useState } from 'react';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { Clear, Search } from '@mui/icons-material';

const DEBOUNCE_MS = 200;

interface TrailerSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
}

// Holds the typed text locally and only reports it after a short pause, so
// typing doesn't re-render the whole trailers table on every keystroke.
const TrailerSearchField = ({ value, onChange }: TrailerSearchFieldProps) => {
  const [text, setText] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Pick up changes made outside this field (filter chips, clear, URL).
  useEffect(() => {
    clearTimeout(timer.current);
    setText(value);
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleChange = (next: string) => {
    setText(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => onChange(next), DEBOUNCE_MS);
  };

  const handleClear = () => {
    clearTimeout(timer.current);
    setText('');
    onChange('');
  };

  return (
    <TextField
      size="small"
      placeholder="Search trailers, status, shipment ref, notes..."
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      sx={{
        minWidth: { xs: 180, sm: 300 },
        maxWidth: { xs: 200, sm: 'none' }
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: text && (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear} edge="end">
              <Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );
};

export default TrailerSearchField;
